import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildDeck, deckStep } from '../../domain/cars/carDeck';

/**
 * Browsing the garage as a deck.
 *
 * The screen composes a car page; this is how one is left for the next, and it
 * is a use case rather than a component's business: which car a swipe deals,
 * whether the card has to leave the screen first, and what that does to history.
 *
 * `deck` is recomputed from the URL every render and carries no state at all —
 * the car in the path plus the filters in the query are a complete description
 * of where in the ring the reader is.
 */

/**
 * How long the card in front takes to leave the screen.
 *
 * READ FROM `--duration-deal` rather than declared here, because both halves
 * need it: the stylesheet runs the animation and this module has to wait for it
 * before navigating. One number, in `tokens.css`, instead of two that drift —
 * and a designer retuning the deal in the stylesheet retunes the wait with it.
 *
 * The fallback is the token's own value, for the one case where there is no
 * computed style to read: jsdom, where the wait still has to be a number.
 */
const DEAL_MS_FALLBACK = 320;

export function readDealDuration(windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject || typeof windowObject.getComputedStyle !== 'function') {
        return DEAL_MS_FALLBACK;
    }

    const declared = windowObject
        .getComputedStyle(windowObject.document.documentElement)
        .getPropertyValue('--duration-deal')
        .trim();
    const milliseconds = declared.endsWith('ms')
        ? Number.parseFloat(declared)
        : Number.parseFloat(declared) * 1000;

    return Number.isFinite(milliseconds) && milliseconds > 0 ? milliseconds : DEAL_MS_FALLBACK;
}

/** Does this reader want the card dealt, or just swapped? */
function prefersReducedMotion(windowObject = typeof window === 'undefined' ? null : window) {
    return Boolean(windowObject)
        && typeof windowObject.matchMedia === 'function'
        && windowObject.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function useCarDeck({ cars, carId, filters = null, localeCode, languageCode }) {
    const navigate = useNavigate();
    // The query carries the filters, and the deck's membership is the filtered
    // shelf — so it has to survive the step, or the second swipe would deal from
    // a different deck than the first.
    const { search } = useLocation();
    // Which way the card in front is leaving, while it is leaving.
    const [dealing, setDealing] = useState(null);
    const dealingRef = useRef(null);
    const deck = buildDeck(cars, carId, { filters, languageCode });

    // A backstop for a car that arrives by some other route while a card is
    // mid-deal — a deck control, a link out of the spec sheet. The deal's own
    // handover clears this in the same commit as the navigation, which is what
    // keeps the arriving card from being drawn at the departing one's position
    // for a frame.
    useEffect(() => setDealing(null), [carId]);
    // A card half-dealt when the screen goes has a timer pointing at a navigate
    // that must not happen.
    useEffect(() => () => window.clearTimeout(dealingRef.current), []);

    const step = useCallback((direction) => {
        const next = deckStep(deck, direction);

        // Nowhere to go happens only in a shelf of one, where every step would
        // land back on the card already showing. Everywhere else the deck is a
        // ring and a swipe always deals something.
        //
        // And nothing while a card is already on its way out: a second swipe
        // mid-deal would navigate twice and skip a car.
        if (!next || dealingRef.current) {
            return;
        }

        // A REPLACE, not a push. A deck is one place and the URL is a bookmark
        // of where the reader is inside it, so one back press leaves for the
        // garage with its remembered scroll rather than unwinding five swipes.
        const arrive = () => navigate(
            { pathname: `/${localeCode}/cars/${next.id}`, search },
            { replace: true },
        );

        if (prefersReducedMotion()) {
            arrive();

            return;
        }

        // The card leaves BEFORE the car changes. The alternative — navigate now
        // and animate the arrival — swaps the card's contents while it is still
        // on screen, so the car being dealt away is the wrong one for the last
        // frames of its own exit.
        dealingRef.current = window.setTimeout(() => {
            dealingRef.current = null;
            // Cleared in the SAME batch as the navigation, not after it. React
            // groups these into one commit, so the new car and the card's
            // resting position land together. Left to an effect on `carId`, the
            // clearing happens a render LATER — which commits one frame with the
            // arriving car drawn at the departing card's position, off the side
            // of the screen, before snapping back. That frame is the flicker.
            setDealing(null);
            arrive();
        }, readDealDuration());
        setDealing(direction);
    }, [deck, localeCode, navigate, search]);

    return { deal: readDealDuration(), deck, dealing, step };
}
